import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  UsersIcon,
  MapIcon,
  KeyIcon,
  ServerStackIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface DashboardStats {
  users: { total: number; admins: number; activeToday: number };
  farms: { total: number; withAlerts: number };
  jobs: { pending: number; completed: number; failed: number };
  api: { configured: number; total: number };
}

interface HealthStatus {
  overall: string;
  services: Record<string, { status: string; message?: string; latency?: number }>;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        const headers = { Authorization: `Bearer ${token}` };

        const [dashboardRes, healthRes] = await Promise.all([
          axios.get(`${baseUrl}/api/v1/admin/dashboard`, { headers }),
          axios.get(`${baseUrl}/api/v1/admin/health`, { headers }),
        ]);

        setStats(dashboardRes.data.stats);
        setHealth(healthRes.data);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-48"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Total Users',
      value: stats?.users.total || 0,
      subtext: `${stats?.users.admins || 0} admins`,
      icon: UsersIcon,
      color: 'blue',
      link: '/admin/users',
    },
    {
      name: 'Total Farms',
      value: stats?.farms.total || 0,
      subtext: `${stats?.farms.withAlerts || 0} with alerts`,
      icon: MapIcon,
      color: 'green',
      link: '/dashboard',
    },
    {
      name: 'API Credentials',
      value: `${stats?.api.configured || 0}/${stats?.api.total || 7}`,
      subtext: 'configured',
      icon: KeyIcon,
      color: 'purple',
      link: '/admin/api-config',
    },
    {
      name: 'System Health',
      value: health?.overall === 'healthy' ? 'Healthy' : 'Degraded',
      subtext: Object.keys(health?.services || {}).length + ' services',
      icon: ServerStackIcon,
      color: health?.overall === 'healthy' ? 'green' : 'yellow',
      link: '/admin/monitoring',
    },
  ];

  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    yellow: 'bg-yellow-50 text-yellow-600',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-500 mt-1">System overview and quick actions</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                <p className="text-sm text-gray-400 mt-1">{stat.subtext}</p>
              </div>
              <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
                <stat.icon className="h-6 w-6" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Service Health */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(health?.services || {}).map(([name, service]) => (
            <div
              key={name}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                service.status === 'healthy'
                  ? 'border-green-200 bg-green-50'
                  : service.status === 'reachable'
                  ? 'border-yellow-200 bg-yellow-50'
                  : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-center">
                {service.status === 'healthy' ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3" />
                ) : (
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-3" />
                )}
                <div>
                  <p className="font-medium text-gray-900 capitalize">
                    {name.replace(/_/g, ' ')}
                  </p>
                  {service.latency && (
                    <p className="text-xs text-gray-500">{service.latency}ms</p>
                  )}
                </div>
              </div>
              <span
                className={`text-xs font-medium px-2 py-1 rounded ${
                  service.status === 'healthy'
                    ? 'bg-green-100 text-green-800'
                    : service.status === 'reachable'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {service.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/admin/api-config"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
          >
            <KeyIcon className="h-8 w-8 text-green-600 mr-4" />
            <div>
              <p className="font-medium text-gray-900">Configure APIs</p>
              <p className="text-sm text-gray-500">Set up satellite & weather APIs</p>
            </div>
          </Link>
          <Link
            to="/admin/users"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <UsersIcon className="h-8 w-8 text-blue-600 mr-4" />
            <div>
              <p className="font-medium text-gray-900">Manage Users</p>
              <p className="text-sm text-gray-500">Add, edit, or remove users</p>
            </div>
          </Link>
          <Link
            to="/admin/logs"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
          >
            <ServerStackIcon className="h-8 w-8 text-purple-600 mr-4" />
            <div>
              <p className="font-medium text-gray-900">View Logs</p>
              <p className="text-sm text-gray-500">Check admin activity logs</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
